import { Scene, Vector3, IDisposable, ArcRotateCamera, AbstractMesh, Observer, Scalar, TransformNode } from '@babylonjs/core';

import { assertNonNull } from '../utils';
import { BALL_FOLLOW_CONFIG, CameraState, INITIAL_CAMERA_CONFIG } from '../models/main-camera-config';

/** Main camera type. */
export type MainCameraType = ArcRotateCamera;

/** App main camera. */
export class MainCamera implements IDisposable {

	/** Camera instance. */
	public readonly instance: MainCameraType;

	private followingMesh: AbstractMesh | null = null;

	private state: CameraState = CameraState.Idle;

	private updateCameraObservable: Observer<Scene> | null = null;

	/** Transform node for camera positioning during ball following. */
	private followCameraNode: TransformNode | null = null;

	/** Pivot point for camera rotation during ball following. */
	private followCameraPivot: TransformNode | null = null;

	public constructor(private readonly scene: Scene) {
		this.instance = this.initializeCamera();
	}

	/** @inheritdoc */
	public dispose(): void {
		this.disposeObserver();
		this.disposeTransformNodes();
		this.instance.dispose();
	}

	private initializeCamera(): MainCameraType {
		const camera = new ArcRotateCamera(
			'arcRotateCamera',
			INITIAL_CAMERA_CONFIG.alpha,
			INITIAL_CAMERA_CONFIG.beta,
			INITIAL_CAMERA_CONFIG.radius,
			INITIAL_CAMERA_CONFIG.target.clone(),
			this.scene,
		);

		camera.lowerRadiusLimit = INITIAL_CAMERA_CONFIG.minRadius;
		camera.upperRadiusLimit = INITIAL_CAMERA_CONFIG.maxRadius;
		camera.lowerBetaLimit = INITIAL_CAMERA_CONFIG.minBeta;
		camera.upperBetaLimit = INITIAL_CAMERA_CONFIG.maxBeta;
		camera.inertia = INITIAL_CAMERA_CONFIG.inertia;
		camera.attachControl();

		assertNonNull(this.scene.activeCameras);
		this.scene.activeCameras.push(camera);

		return camera;
	}

	private disposeObserver(): void {
		if (this.updateCameraObservable) {
			this.scene.onBeforeRenderObservable.remove(this.updateCameraObservable);
			this.updateCameraObservable = null;
		}
	}

	private disposeTransformNodes(): void {
		if (this.followCameraNode) {
			this.followCameraNode.dispose();
			this.followCameraNode = null;
		}

		if (this.followCameraPivot) {
			this.followCameraPivot.dispose();
			this.followCameraPivot = null;
		}
	}

	/** Reset the camera position to initial state. */
	public resetPosition(): void {
		this.instance.alpha = INITIAL_CAMERA_CONFIG.alpha;
		this.instance.beta = INITIAL_CAMERA_CONFIG.beta;
		this.instance.radius = INITIAL_CAMERA_CONFIG.radius;
	}

	/**
	 * Lock camera to follow a mesh (e.g., golf ball).
	 * Camera will automatically follow the mesh position.
	 * @param mesh Mesh to follow.
	 */
	public lockToMesh(mesh: AbstractMesh): void {
		this.instance.lockedTarget = mesh;
		this.followingMesh = mesh;
	}

	/** Unlock camera from following a mesh. */
	public unlockFromMesh(): void {
		this.instance.lockedTarget = null;
		this.followingMesh = null;
	}

	/**
	 * Enable smooth ball following mode during ball flight.
	 * Camera detaches from user control and smoothly follows the ball using transform nodes.
	 */
	public enableBallFollowing(): void {
		if (this.state === CameraState.FollowingBall || !this.followingMesh) {
			return;
		}

		this.state = CameraState.FollowingBall;
		this.setupTransformNodes();
		this.initializeCameraPositionForFollowing();
		this.instance.detachControl();
		this.instance.useAutoRotationBehavior = true;
		this.startFollowingUpdate();
	}

	/** Disable ball following mode and restore user controls. */
	public disableBallFollowing(): void {
		if (this.state !== CameraState.FollowingBall) {
			return;
		}

		this.state = CameraState.Idle;
		this.disposeTransformNodes();
		this.instance.attachControl();
		this.instance.useAutoRotationBehavior = false;
		this.disposeObserver();
	}

	/**
	 * Set up transform nodes for smooth ball following.
	 * Creates a parent-child hierarchy: followingMesh -> followCameraNode -> followCameraPivot.
	 */
	private setupTransformNodes(): void {
		this.followCameraNode = new TransformNode('mainFollowCamera', this.scene);
		this.followCameraPivot = new TransformNode('mainFollowCameraPivot', this.scene);
		this.followCameraPivot.parent = this.followCameraNode;
		this.followCameraNode.parent = this.followingMesh;
		this.instance.lockedTarget = null;
	}

	/**
	 * Initialize camera position when starting ball following.
	 * If camera is too far from ball, repositions it at optimal distance.
	 */
	private initializeCameraPositionForFollowing(): void {
		if (!this.followingMesh) {
			return;
		}

		const ballPosition = this.followingMesh.getAbsolutePosition();
		const currentPosition = this.instance.position;
		const directionToBall = ballPosition.subtract(currentPosition);
		const currentDistance = directionToBall.length();

		// If camera is too far, position it at optimal distance
		if (currentDistance > BALL_FOLLOW_CONFIG.followRadius * 1.5) {
			const direction = directionToBall.normalize();
			const idealPosition = ballPosition.subtract(direction.scale(BALL_FOLLOW_CONFIG.followRadius));
			this.instance.position = idealPosition;
		}

		this.instance.setTarget(ballPosition);
	}

	/**
	 * Start continuous camera update during ball following.
	 * Registers observer that runs on every frame to smoothly follow the ball.
	 */
	private startFollowingUpdate(): void {
		this.updateCameraObservable = this.scene.onBeforeRenderObservable.add(() => {
			if (this.state === CameraState.FollowingBall && this.followCameraPivot) {
				this.updateBallFollowPosition();
			}
		});
	}

	/**
	 * Update camera position to follow the ball smoothly.
	 * Interpolates camera position to pivot point and adjusts radius for cinematic effect.
	 */
	private updateBallFollowPosition(): void {
		if (!this.followingMesh || !this.followCameraPivot) {
			return;
		}

		const ballPosition = this.followingMesh.getAbsolutePosition().clone();
		const pivotPosition = this.followCameraPivot.getAbsolutePosition().clone();

		this.instance.setTarget(ballPosition);
		this.interpolateCameraPosition(pivotPosition);
		this.interpolateCameraRadius();
	}

	/**
	 * Smoothly interpolate camera position towards target position.
	 * @param targetPosition Target position for camera to move towards.
	 */
	private interpolateCameraPosition(targetPosition: Vector3): void {
		this.instance.position = Vector3.Lerp(
			this.instance.position,
			targetPosition,
			BALL_FOLLOW_CONFIG.lerpSpeed,
		);
	}

	/**
	 * Smoothly interpolate camera radius to optimal following distance.
	 * Only adjusts if current radius is less than target radius.
	 */
	private interpolateCameraRadius(): void {
		if (this.instance.radius >= BALL_FOLLOW_CONFIG.followRadius) {
			return;
		}

		this.instance.radius = Scalar.Lerp(
			this.instance.radius,
			BALL_FOLLOW_CONFIG.followRadius,
			BALL_FOLLOW_CONFIG.lerpSpeed,
		);
	}
}
